import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { AlertTriangle, CheckCircle2, ChevronDown, ImageOff, Plus } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'
import { cn } from '@/shared/lib/utils'
import { Badge } from '@/shared/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs'
import { t } from '@/shared/lib/i18n'
import { complianceBgColor, formatCompliancePercent } from '@/shared/lib/compliance'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  cloneInstrumentPath,
  complianceDashboardPath,
  editInstrumentPath,
  instrumentLotInstrumentLotLogPath,
  instrumentPath,
  instrumentTaskInstrumentLotLogPath,
  instrumentsPath,
  retireInstrumentLotPath,
  statusUpdateFormInstrumentPath,
  transferFormInstrumentPath,
} from '@/shared/lib/routes'
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/shared/ui/select'
import DataPagination from '@/shared/ui/DataPagination'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import { TaskStatusBadge } from '@/features/task-status'
import { CommentsPanel } from '@/features/comments/CommentsPanel'
import {
  type LifecycleStatus,
  LIFECYCLE_BADGE,
  isUnusableLifecycleStatus,
} from '@/entities/lot/model/lifecycleStatus'
import { LotsExpireWarning } from '@/entities/lot/ui/LotsExpireWarning'
import { Label } from '@/shared/ui/label'
import { Switch } from '@/shared/ui/switch'
import { RetireLotDetailDialog } from '@/features/instrument-lots/RetireLotDetailDialog'
import { LotFormDialog } from '@/features/instrument-lots/LotFormDialog'
import { LotCompareDialog } from '@/features/instrument-lots/LotCompareDialog'
import { LotNotesBanner } from '@/features/instrument-lots/LotNotesBanner'
import {
  DEFAULT_LOT_DETAIL_VISIBILITY,
  type LotDetailVisibilityCheckboxState,
  lotDetailPassesVisibilityFilters,
} from '@/features/instrument-lots/lotDetailVisibilityFilters'
import { useCan } from '@/shared/hooks/useCan'
import { useStatusPoll } from '@/shared/hooks/useStatusPoll'
import { STATUS_POLL_INTERVAL_MS } from '@/shared/constants/statusPolling'
import { Resources, Actions } from '@/shared/rbac'
import type { CurrentUserInfo } from '@/shared/types/CurrentUserInfo'
import type {
  WebInstrumentCalendarView,
  WebInstrumentServiceReport,
  WebInstrumentShow,
  WebInstrumentDepartmentLog,
  WebInstrumentStatusLog,
  WebInstrumentsLotIndex,
  WebComment,
} from '@/shared/types/web'
import ReportsTabContent from './components/ReportsTab/ReportsTabContent'
import LogDetailsDialog from './components/ReportsTab/LogDetailsDialog'
import { reloadInstrumentAndScheduleAfterVoid } from './components/ReportsTab/calendarLogDeleteRefresh'
import InstrumentTasksAccordion from './components/TasksTab/InstrumentTasksAccordion'
import InstrumentTypeDocumentsDisclosure from './components/InstrumentTypeDocumentsDisclosure'
import AddServiceReportDialog from './components/AddServiceReportDialog'
import type WebInstrumentsCurrentScheduleTask from '@/shared/types/web/WebInstrumentsCurrentScheduleTask'
import {
  ChangeLogVersionCard,
  type TaskVersionEntry,
} from './components/ReportsTab/TaskChangeLogDialog'
import { partitionTaskHistoryLogs } from './taskHistoryAlertFilter'
import { formatCalendarDate } from '@/shared/lib/tenantDateTime'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

const VALID_TABS = ['tasks', 'lots', 'reports', 'compliance', 'history', 'info'] as const
type TabValue = (typeof VALID_TABS)[number]
/** Props the Tasks tab keeps fresh: status badges plus the per-task compliance windows. */
const TASKS_TAB_LIVE_PROPS = ['instrument', 'currentSchedule']
const INSTRUMENT_REPORTS_TAB_REGION_SELECTOR = '[data-instrument-reports-tab]'

// Keep the Inertia history entry (page payload + scroll) when only the tab hash changes.
// replaceState(null, ...) wipes that entry, and the in-flight partial reload then
// replaceStates page.url and drops the hash.
const writeActiveTabHash = (value: string) => {
  const nextUrl = new URL(window.location.href)
  nextUrl.hash = value
  window.history.replaceState(window.history.state, '', nextUrl)
}

const revealInstrumentReportsTab = () => {
  const regions = [
    ...document.querySelectorAll<HTMLElement>(INSTRUMENT_REPORTS_TAB_REGION_SELECTOR),
  ]
  const visibleRegion = regions.find((region) => region.getClientRects().length > 0) ?? regions[0]
  if (!visibleRegion) return

  visibleRegion.scrollIntoView({ block: 'start' })
  const reportsPanel =
    visibleRegion.querySelector<HTMLElement>('[role="tabpanel"]:not([hidden])') ??
    visibleRegion.querySelector<HTMLElement>('[data-instrument-reports-panel]')
  reportsPanel?.focus({ preventScroll: true })
}

const PAGE_SIZE = 10

// Legacy parity: "Unresolved Issue" is system-managed. While it is the current status the
// manual status change is disabled — it clears when a passing QC result is logged.
const UNRESOLVED_ISSUE_STATUS_CODE = 'URDI'

function buildMeta<T>(items: T[], page: number): PaginationMeta {
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
  return { page: Math.min(page, totalPages), limit: PAGE_SIZE, total: items.length, totalPages }
}

function getPage<T>(items: T[], page: number): T[] {
  const safePage = Math.min(page, Math.max(1, Math.ceil(items.length / PAGE_SIZE)))
  return items.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
}

// complianceColor and formatCompliancePercent imported from @/shared/lib/compliance

type CurrentScheduleProp = {
  tasks: WebInstrumentsCurrentScheduleTask[]
  canTest: boolean
  instrumentStatusLabel: string | null
  loggerOptions: { id: number; label: string }[]
  logAuthenticationCode: string
  siteTimeZone?: string | null
}

interface Props {
  instrument: WebInstrumentShow
  lots: WebInstrumentsLotIndex[]
  departmentLogs: WebInstrumentDepartmentLog[]
  statusLogs: WebInstrumentStatusLog[]
  comments?: WebComment[]
  reportsCalendar?: WebInstrumentCalendarView | null
  reportsSiteTimeZone?: string
  reportsSiteToday?: string
  serviceReports?: WebInstrumentServiceReport[]
  currentUserInfo?: CurrentUserInfo | null
  currentSchedule?: CurrentScheduleProp | null
  logAuthenticationCode?: string | null
  taskVersionHistory?: TaskVersionEntry[] | null
  serviceReportLoggerOptions?: { id: number; label: string }[]
  serviceReportAllowedFileTypes?: { mimeTypes: string[]; extensions: string[] }
  [key: string]: unknown
}

const MobileField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-start justify-between gap-3 py-1">
    <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
    <span className="text-right text-sm">{children}</span>
  </div>
)

const InstrumentShowPage = () => {
  const { formatDateTime } = useSiteDateTime()
  const {
    instrument,
    lots,
    departmentLogs,
    statusLogs,
    comments,
    reportsCalendar,
    reportsSiteTimeZone,
    reportsSiteToday,
    serviceReports,
    currentSchedule,
    logAuthenticationCode,
    taskVersionHistory,
    serviceReportLoggerOptions,
    serviceReportAllowedFileTypes,
    errors: pageErrors,
  } = usePage<Props>().props

  const { complianceSettings, tenantTimeZone } =
    usePage<import('@/shared/types/SharedProps').SharedProps>().props

  const displayTimeZone = reportsSiteTimeZone ?? tenantTimeZone

  const canReadInstrumentReports = useCan(Resources.COMPLIANCE_REPORT, Actions.READ)
  const canEdit = useCan(Resources.INSTRUMENT_MAINTENANCE, Actions.UPDATE)
  // Clone creates a brand-new instrument, so it is gated by CREATE to match the
  // backend policy (clone? = create?) — not UPDATE.
  const canClone = useCan(Resources.INSTRUMENT_MAINTENANCE, Actions.CREATE)
  const canTransfer = useCan(Resources.INSTRUMENT_DEPARTMENT, Actions.UPDATE)
  const canUpdateStatus = useCan(Resources.INSTRUMENT_STATUS, Actions.CREATE)
  const isStatusChangeLocked = instrument.statusCode === UNRESOLVED_ISSUE_STATUS_CODE
  const canCreateLot = useCan(Resources.LOT_MANAGEMENT, Actions.CREATE)
  const canRetireLot = useCan(Resources.LOT_MANAGEMENT, Actions.UPDATE)
  const canConfigureTaskDataFields = useCan(Resources.INSTRUMENT_TASK_DATA_FIELD, Actions.CREATE)
  const canOpenTaskLog = useCan(Resources.INSTRUMENT_TASK_LOG, Actions.READ)
  const canCreateTaskLog = useCan(Resources.INSTRUMENT_TASK_LOG, Actions.CREATE)
  const canCreateServiceReport = useCan(Resources.INSTRUMENT_SERVICE_REPORT, Actions.CREATE)
  const canPersistInstrumentLotSelectionForInstrumentTasksUi = useCan(
    Resources.LOT_MANAGEMENT,
    Actions.UPDATE,
  )

  const [serviceReportDialogOpen, setServiceReportDialogOpen] = useState(false)

  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const hash = window.location.hash.slice(1) as TabValue
    return VALID_TABS.includes(hash) ? hash : 'tasks'
  })
  // True once the address bar hash is the source of the active tab (deep link or a tab click).
  // A bare Show load stays hash-less so we don't write #tasks on every visit.
  const tabHashCommittedRef = useRef(window.location.hash.slice(1) !== '')

  useEffect(() => {
    if (!tabHashCommittedRef.current) return
    const pagePath = window.location.pathname

    const restoreTabHash = () => {
      if (!tabHashCommittedRef.current) return
      // A finished visit to another page must not inherit this tab hash.
      if (window.location.pathname !== pagePath) return
      if (window.location.hash === `#${activeTab}`) return
      writeActiveTabHash(activeTab)
    }

    document.addEventListener('inertia:finish', restoreTabHash)
    return () => document.removeEventListener('inertia:finish', restoreTabHash)
  }, [activeTab])

  // The current-schedule payload is lazy on the server (skip-on-default, like reports).
  // Fetch it the first time the Tasks tab is shown so the page isn't paying for it on
  // every Show load. Inline log submits refresh the same prop via a partial reload.
  const scheduleReloadInFlight = useRef(false)
  useEffect(() => {
    if (activeTab !== 'tasks' || currentSchedule || scheduleReloadInFlight.current) return
    scheduleReloadInFlight.current = true
    router.reload({
      only: ['currentSchedule'],
      onFinish: () => {
        scheduleReloadInFlight.current = false
      },
    })
  }, [activeTab, currentSchedule])

  // task_version_history is lazy on the server (skip-on-default, like reports and currentSchedule).
  // Fetch it the first time the History tab is shown.
  const historyReloadInFlight = useRef(false)
  useEffect(() => {
    if (activeTab !== 'history' || taskVersionHistory || historyReloadInFlight.current) return
    historyReloadInFlight.current = true
    router.reload({
      only: ['taskVersionHistory'],
      onFinish: () => {
        historyReloadInFlight.current = false
      },
    })
  }, [activeTab, taskVersionHistory])

  // Tab-gated status polling (AC1, AC2, AC3, AC8) — useStatusPoll hard-pauses while the
  // tab/window is hidden and additionally only runs while the matching page tab is active.
  useStatusPoll(
    STATUS_POLL_INTERVAL_MS,
    { only: TASKS_TAB_LIVE_PROPS, onNetworkError: () => false },
    activeTab === 'tasks',
  )
  useStatusPoll(
    STATUS_POLL_INTERVAL_MS,
    { only: ['instrument'], onNetworkError: () => false },
    activeTab === 'compliance',
  )

  useBreadcrumbs([
    { label: t('nav.instruments'), href: instrumentsPath() },
    ...(activeTab === 'info'
      ? [{ label: instrument.instrumentLabel }]
      : [
          { label: instrument.instrumentLabel, href: instrumentPath(instrument.id) },
          { label: t(`instruments.show.tabs.${activeTab}` as const) },
        ]),
  ])

  const [deptLogPage, setDeptLogPage] = useState(1)
  const [statusLogPage, setStatusLogPage] = useState(1)
  const [lotsPage, setLotsPage] = useState(1)
  const [instrumentLotsTabVisibilityCheckboxDraft, setInstrumentLotsTabVisibilityCheckboxDraft] =
    useState<LotDetailVisibilityCheckboxState>(DEFAULT_LOT_DETAIL_VISIBILITY)
  const [
    scheduledRetirementTargetLotDetailIdOnInstrumentLotsTab,
    setScheduledRetirementTargetLotDetailIdOnInstrumentLotsTab,
  ] = useState<number | null>(null)

  type LotDialogState =
    | { open: false }
    | { open: true; lotId: number; cloneLotDetailId?: number }
    | { open: true; lotDetailId: number }
  const [lotDialogState, setLotDialogState] = useState<LotDialogState>({ open: false })

  const [compareDialogLotDetailId, setCompareDialogLotDetailId] = useState<number | null>(null)
  const [
    persistDefaultLotDetailSelectionRequestInFlight,
    setPersistDefaultLotDetailSelectionRequestInFlight,
  ] = useState(false)

  const reloadInstrumentCardInstrumentAndLotsPayloadFromServer = () => {
    router.reload({ only: ['instrument', 'lots'] })
  }

  // instrumentPlanningTaskPrimaryKey is null for a Lot with zero task references (legacy parity,
  // lotInfo.component.ts:912 — instrumentTaskId is optional context, never a Select precondition):
  // POST to the task-less (instrument, lot) endpoint instead of the task-scoped one.
  const persistDefaultLotDetailSelectionForControlsReagentsPlanningTaskOnLotsTab = (
    instrumentPlanningTaskPrimaryKey: number | null,
    targetedLotDetailPrimaryKey: number,
  ) => {
    setPersistDefaultLotDetailSelectionRequestInFlight(true)
    const path =
      instrumentPlanningTaskPrimaryKey != null
        ? instrumentTaskInstrumentLotLogPath(instrument.id, instrumentPlanningTaskPrimaryKey)
        : instrumentLotInstrumentLotLogPath(instrument.id, targetedLotDetailPrimaryKey)
    const body =
      instrumentPlanningTaskPrimaryKey != null
        ? { instrument_lot_log: { lot_detail_id: targetedLotDetailPrimaryKey } }
        : {}
    router.post(path, body, {
      preserveScroll: true,
      onFinish: () => setPersistDefaultLotDetailSelectionRequestInFlight(false),
      onSuccess: reloadInstrumentCardInstrumentAndLotsPayloadFromServer,
    })
  }
  const [openHistorySections, setOpenHistorySections] = useState<Set<string>>(
    () => new Set<string>(),
  )
  const [showTaskHistoryAlertsOnly, setShowTaskHistoryAlertsOnly] = useState(false)

  const toggleHistorySection = (key: string) =>
    setOpenHistorySections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })

  const commitActiveTab = (value: string) => {
    tabHashCommittedRef.current = true
    setActiveTab(value as TabValue)
    writeActiveTabHash(value)
    // The cached schedule may be stale from changes made while the user was on another tab;
    // the first-ever activation is covered by the lazy-load effect instead.
    if (value === 'tasks' && value !== activeTab && currentSchedule) {
      router.reload({ only: TASKS_TAB_LIVE_PROPS })
    }
  }

  const handleTabChange = (value: string) => {
    const scrollY = window.scrollY
    commitActiveTab(value)
    requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: 'instant' }))
  }

  // The warning sits in the header. Restoring scrollY leaves the Reports tab below the
  // fold on short viewports, so the click looks like it did nothing.
  const openReportsTabFromFailureWarning = () => {
    commitActiveTab('reports')
    requestAnimationFrame(() => revealInstrumentReportsTab())
  }

  // REBUILD-554: View Details opens the exact failed occurrence (latest open failure, or the
  // latest failure in the window once all are resolved) instead of the month calendar. Older
  // payloads without a target fall back to the Reports tab.
  const failureTargetLogId = instrument.failureTargetLogId ?? null
  const failureTargetDate = instrument.failureTargetDate ?? null
  const hasFailureTarget = failureTargetLogId !== null && failureTargetDate !== null
  const [failureDetailsOpen, setFailureDetailsOpen] = useState(false)
  const openFailureDetails = () => {
    if (hasFailureTarget) {
      setFailureDetailsOpen(true)
      return
    }
    openReportsTabFromFailureWarning()
  }
  const failuresAllResolved = (instrument.openFailureCount ?? 0) === 0

  // ─── Tab content ─────────────────────────────────────────────────────────────
  // Shared between mobile accordion and desktop tabs via tabContent record.

  const infoContent = (
    <div className="rounded-lg border p-4 sm:p-6">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
        {[
          [t('instruments.show.info.type'), instrument.instrumentTypeName],
          [t('instruments.show.info.manufacturer'), instrument.manufacturer],
          [t('instruments.show.info.model'), instrument.manufacturerModel],
          [t('instruments.show.info.description'), instrument.description],
          [t('instruments.show.info.serialNumber'), instrument.serialNumber],
          [t('instruments.show.info.barcode'), instrument.barcodeId],
          [t('instruments.show.info.department'), instrument.departmentName],
          [t('instruments.show.info.unit'), instrument.unitName],
          [t('instruments.show.info.site'), instrument.siteName],
          [
            t('instruments.show.info.dateOfPurchase'),
            instrument.dateOfPurchase ? formatCalendarDate(instrument.dateOfPurchase) : null,
          ],
          [
            t('instruments.show.info.cost'),
            instrument.cost != null ? String(instrument.cost) : null,
          ],
          [
            t('instruments.show.info.compliancePercent'),
            instrument.rollingCompliancePercent != null
              ? formatCompliancePercent(instrument.rollingCompliancePercent)
              : null,
          ],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
            <dd className="text-sm">{value ?? t('instruments.show.noData')}</dd>
          </div>
        ))}
        <div>
          <dt className="text-sm font-medium text-muted-foreground">
            {t('instruments.show.info.status')}
          </dt>
          <dd className="text-sm">
            {instrument.statusLabel ? (
              <Badge variant="secondary">{instrument.statusLabel}</Badge>
            ) : (
              t('instruments.show.noData')
            )}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-muted-foreground">
            {t('instruments.show.info.tags')}
          </dt>
          <dd className="mt-1 flex flex-wrap gap-1">
            {instrument.instrumentTags.length > 0
              ? instrument.instrumentTags.map((tag) => (
                  <Badge key={tag.id} variant="secondary">
                    {tag.name}
                  </Badge>
                ))
              : t('instruments.show.noData')}
          </dd>
        </div>
      </dl>
      {comments && (
        <div className="mt-6">
          <CommentsPanel
            comments={comments}
            commentableType="Instrument"
            commentableId={instrument.id}
          />
        </div>
      )}
    </div>
  )

  // Bump this counter whenever a task log is submitted or voided from the Tasks tab so the
  // Reports tab calendar silently re-fetches the current month (REBUILD-335).
  const [calendarRefreshSignal, setCalendarRefreshSignal] = useState(0)
  const bumpCalendarRefreshSignal = () => setCalendarRefreshSignal((prev) => prev + 1)

  const tasksContent = (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <InstrumentTypeDocumentsDisclosure documents={instrument.instrumentTypeDocuments} />
        {canCreateServiceReport && (
          <Button onClick={() => setServiceReportDialogOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('instruments.show.serviceReports.addButton')}
          </Button>
        )}
      </div>

      {currentSchedule ? (
        <InstrumentTasksAccordion
          instrumentId={Number(instrument.id)}
          instrumentLabel={instrument.instrumentLabel}
          currentSchedule={currentSchedule}
          lots={lots}
          errors={pageErrors as Record<string, string | string[]> | undefined}
          canOpenTaskLog={canOpenTaskLog}
          canConfigureTaskDataFields={canConfigureTaskDataFields}
          canPersistLotSelection={canPersistInstrumentLotSelectionForInstrumentTasksUi}
          canRetireLot={canRetireLot}
          canSubmitTaskLog={canCreateTaskLog}
          logAuthenticationCode={currentSchedule.logAuthenticationCode ?? logAuthenticationCode}
          onAfterTaskLog={bumpCalendarRefreshSignal}
        />
      ) : (
        <p className="py-4 text-sm text-muted-foreground">{t('instruments.show.tasks.loading')}</p>
      )}
    </div>
  )

  const alwaysVisibleLotDetailIdsDerivedFromPersistedInstrumentTaskSelections = useMemo(() => {
    const uniqueSelectableLotDetailIdSet = new Set<number>()
    for (const persistedInstrumentTaskRow of instrument.tasks) {
      const candidatePersistentSelectionLotDetailId = persistedInstrumentTaskRow.selectedLotDetailId
      if (candidatePersistentSelectionLotDetailId != null) {
        uniqueSelectableLotDetailIdSet.add(candidatePersistentSelectionLotDetailId)
      }
    }
    // Task-less Lots hold their selection on the Lot itself (REBUILD-366/399); exempt those too, so
    // the default-on visibility toggles never hide the supply a Lot is currently selected on.
    for (const groupingLotRecordForAccordion of lots) {
      const candidateTaskLessSelectionLotDetailId =
        groupingLotRecordForAccordion.selectedLotDetailId
      if (candidateTaskLessSelectionLotDetailId != null) {
        uniqueSelectableLotDetailIdSet.add(candidateTaskLessSelectionLotDetailId)
      }
    }
    return Array.from(uniqueSelectableLotDetailIdSet)
  }, [instrument.tasks, lots])

  const accordionLotBucketsFilteredForLotsTabPaging = useMemo(
    () =>
      lots
        .map((groupingLotRecordForAccordion) => ({
          groupingLotRecordForAccordion,
          lotDetailsVisibleThroughLotsTabCheckboxMask:
            groupingLotRecordForAccordion.lotDetails.filter(
              (individualLotDetailVisibilityCandidateRow) =>
                lotDetailPassesVisibilityFilters(
                  {
                    id: individualLotDetailVisibilityCandidateRow.id,
                    lifecycleStatus: individualLotDetailVisibilityCandidateRow.lifecycleStatus,
                  },
                  instrumentLotsTabVisibilityCheckboxDraft,
                  alwaysVisibleLotDetailIdsDerivedFromPersistedInstrumentTaskSelections,
                ),
            ),
        }))
        .filter(
          ({ groupingLotRecordForAccordion, lotDetailsVisibleThroughLotsTabCheckboxMask }) => {
            const catalogLotHasNeverHadLotDetailLines =
              groupingLotRecordForAccordion.lotDetails.length === 0
            const atLeastOneLotDetailPassesCurrentVisibilityToggles =
              lotDetailsVisibleThroughLotsTabCheckboxMask.length > 0
            return (
              catalogLotHasNeverHadLotDetailLines ||
              atLeastOneLotDetailPassesCurrentVisibilityToggles
            )
          },
        ),
    [
      lots,
      instrumentLotsTabVisibilityCheckboxDraft,
      alwaysVisibleLotDetailIdsDerivedFromPersistedInstrumentTaskSelections,
    ],
  )

  const lotsContent = (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground sm:max-w-sm">
          {t('instruments.show.lots.visibilityFilterExplanation')}
        </p>
        <div className="flex flex-wrap items-center gap-6 sm:justify-end">
          <div className="flex items-center gap-2">
            <Switch
              id="instrument-detail-lots-tab-hide-expired"
              checked={instrumentLotsTabVisibilityCheckboxDraft.hideExpiredLots}
              onCheckedChange={(primitiveCheckedIncoming) =>
                setInstrumentLotsTabVisibilityCheckboxDraft((priorDraft) => ({
                  ...priorDraft,
                  hideExpiredLots: primitiveCheckedIncoming === true,
                }))
              }
            />
            <Label
              htmlFor="instrument-detail-lots-tab-hide-expired"
              className="cursor-pointer text-sm"
            >
              {t('instruments.show.lots.toggleHideExpired')}
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="instrument-detail-lots-tab-hide-retired"
              checked={instrumentLotsTabVisibilityCheckboxDraft.hideRetiredLots}
              onCheckedChange={(primitiveCheckedIncoming) =>
                setInstrumentLotsTabVisibilityCheckboxDraft((priorDraft) => ({
                  ...priorDraft,
                  hideRetiredLots: primitiveCheckedIncoming === true,
                }))
              }
            />
            <Label
              htmlFor="instrument-detail-lots-tab-hide-retired"
              className="cursor-pointer text-sm"
            >
              {t('instruments.show.lots.toggleHideRetired')}
            </Label>
          </div>
        </div>
      </div>
      {accordionLotBucketsFilteredForLotsTabPaging.length === 0 && (
        <p className="py-4 text-sm text-muted-foreground">{t('instruments.show.lots.empty')}</p>
      )}
      {getPage(accordionLotBucketsFilteredForLotsTabPaging, lotsPage).map(
        ({ groupingLotRecordForAccordion, lotDetailsVisibleThroughLotsTabCheckboxMask }) => {
          const candidateTasksForLotGroup = instrument.tasks.filter(
            (task) =>
              task.linkedLotId === groupingLotRecordForAccordion.id ||
              task.usedLotIds.includes(groupingLotRecordForAccordion.id),
          )

          // Legacy parity: the lot selection is scoped to (instrument, lot) and shared across
          // every task using the lot — and exists independently of any task at all (a Lot with
          // zero task references is still selectable, lotInfo.component.ts:912). Any task using
          // the lot can carry the write (the POST instrument_task_id is provenance only) when one
          // exists; the read comes straight from the lot's own selectedLotDetailId either way.
          const instrumentPlanningTaskBackedByControlsReagentsLotBlock =
            candidateTasksForLotGroup[0] ?? null

          const persistedDefaultLotDetailIdForMatchedPlanningTask =
            groupingLotRecordForAccordion.selectedLotDetailId

          const instrumentPlanningTaskPrimaryKeyForDefaultLotAssignment =
            instrumentPlanningTaskBackedByControlsReagentsLotBlock?.id ?? null

          const lotsTabShowsPlanningBackedDefaultLotSelectionColumn =
            canPersistInstrumentLotSelectionForInstrumentTasksUi

          return (
            <div key={groupingLotRecordForAccordion.id} className="space-y-2">
              <div className="rounded-lg border">
                <div className="flex items-center justify-between border-b px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold [overflow-wrap:anywhere]">
                      {groupingLotRecordForAccordion.name}
                    </span>
                    {groupingLotRecordForAccordion.isKit && (
                      <Badge variant="secondary">{t('instruments.lotsIndex.kit')}</Badge>
                    )}
                    {groupingLotRecordForAccordion.requiresValidation && (
                      <Badge variant="outline">
                        {t('instruments.lotsIndex.requiresValidation')}
                      </Badge>
                    )}
                  </div>
                  {canCreateLot && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="ml-2 hidden shrink-0 sm:flex"
                      onClick={() =>
                        setLotDialogState({
                          open: true,
                          lotId: groupingLotRecordForAccordion.id,
                        })
                      }
                    >
                      {t('instruments.lotsIndex.addLotDetail')}
                    </Button>
                  )}
                </div>
                <LotNotesBanner notes={groupingLotRecordForAccordion.notes} className="mx-4 mt-3" />
                {lotDetailsVisibleThroughLotsTabCheckboxMask.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-muted-foreground">
                    {t('instruments.lotsIndex.noLotDetails')}
                  </p>
                ) : (
                  <>
                    {/* Mobile cards */}
                    <div className="divide-y sm:hidden">
                      {lotDetailsVisibleThroughLotsTabCheckboxMask.map((lotDetail) => {
                        const status: LifecycleStatus = lotDetail.lifecycleStatus
                        const expirationDate =
                          lotDetail.openedExpiration ?? lotDetail.expirationDate
                        return (
                          <div
                            key={lotDetail.id}
                            className="cursor-pointer space-y-2 p-3 hover:bg-muted/50"
                            onClick={() =>
                              setLotDialogState({ open: true, lotDetailId: lotDetail.id })
                            }
                          >
                            <div className="flex items-start justify-between gap-2">
                              <span className="font-medium [overflow-wrap:anywhere]">
                                {lotDetail.lotNumber ?? '—'}
                              </span>
                              <Badge variant={LIFECYCLE_BADGE[status] ?? 'outline'}>
                                {t(`instruments.lotsIndex.lifecycleStatus.${status}`)}
                              </Badge>
                            </div>
                            <div className="space-y-0.5">
                              <MobileField label={t('instruments.lotsIndex.table.expiration')}>
                                <span className="inline-flex flex-col items-end gap-1">
                                  <span>
                                    {expirationDate ? formatCalendarDate(expirationDate) : '—'}
                                  </span>
                                  <LotsExpireWarning
                                    expirationDate={expirationDate}
                                    lifecycleStatus={status}
                                    timeZone={displayTimeZone}
                                  />
                                </span>
                              </MobileField>
                              <MobileField label={t('instruments.lotsIndex.table.site')}>
                                {lotDetail.siteName ?? '—'}
                              </MobileField>
                              <MobileField label={t('instruments.lotsIndex.table.received')}>
                                {lotDetail.dateReceived
                                  ? formatCalendarDate(lotDetail.dateReceived)
                                  : '—'}
                              </MobileField>
                              <MobileField label={t('instruments.lotsIndex.table.opened')}>
                                {lotDetail.dateOpened
                                  ? formatCalendarDate(lotDetail.dateOpened)
                                  : '—'}
                              </MobileField>
                            </div>
                            {(lotsTabShowsPlanningBackedDefaultLotSelectionColumn ||
                              canCreateLot ||
                              canRetireLot) && (
                              <div
                                className="flex flex-wrap justify-end gap-2 pt-2"
                                onClick={(interactionEventBubbleGuard) =>
                                  interactionEventBubbleGuard.stopPropagation()
                                }
                              >
                                {/* REBUILD-366: the current selection keeps an enabled Select
                                    button (legacy parity) instead of a dead "Selected" chip. */}
                                {lotsTabShowsPlanningBackedDefaultLotSelectionColumn && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={
                                      persistedDefaultLotDetailIdForMatchedPlanningTask ===
                                      lotDetail.id
                                        ? 'default'
                                        : 'secondary'
                                    }
                                    aria-pressed={
                                      persistedDefaultLotDetailIdForMatchedPlanningTask ===
                                      lotDetail.id
                                    }
                                    disabled={
                                      isUnusableLifecycleStatus(status) ||
                                      persistDefaultLotDetailSelectionRequestInFlight
                                    }
                                    onClick={() =>
                                      persistDefaultLotDetailSelectionForControlsReagentsPlanningTaskOnLotsTab(
                                        instrumentPlanningTaskPrimaryKeyForDefaultLotAssignment,
                                        lotDetail.id,
                                      )
                                    }
                                  >
                                    {persistedDefaultLotDetailIdForMatchedPlanningTask ===
                                    lotDetail.id
                                      ? t('instruments.controlsReagents.selectedButton')
                                      : t('instruments.controlsReagents.selectButton')}
                                  </Button>
                                )}
                                {canCreateLot ? (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                      setLotDialogState({
                                        open: true,
                                        lotId: groupingLotRecordForAccordion.id,
                                        cloneLotDetailId: lotDetail.id,
                                      })
                                    }
                                  >
                                    {t('instruments.controlsReagents.cloneLotButton')}
                                  </Button>
                                ) : null}
                                {canRetireLot ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    disabled={isUnusableLifecycleStatus(status)}
                                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    onClick={() =>
                                      setScheduledRetirementTargetLotDetailIdOnInstrumentLotsTab(
                                        lotDetail.id,
                                      )
                                    }
                                  >
                                    {t('instruments.lotsIndex.retire')}
                                  </Button>
                                ) : null}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                    {canCreateLot && (
                      <div className="border-t px-3 py-2 sm:hidden">
                        <Button
                          variant="outline"
                          size="default"
                          className="w-full"
                          onClick={() =>
                            setLotDialogState({
                              open: true,
                              lotId: groupingLotRecordForAccordion.id,
                            })
                          }
                        >
                          {t('instruments.lotsIndex.addLotDetail')}
                        </Button>
                      </div>
                    )}
                    {/* Desktop table */}
                    <Table className="hidden sm:table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-40">
                            {t('instruments.lotsIndex.table.lotNumber')}
                          </TableHead>
                          <TableHead className="w-24">
                            {t('instruments.lotsIndex.table.status')}
                          </TableHead>
                          <TableHead className="w-32">
                            {t('instruments.lotsIndex.table.expiration')}
                          </TableHead>
                          <TableHead className="w-56">
                            {t('instruments.lotsIndex.table.site')}
                          </TableHead>
                          <TableHead className="w-28">
                            {t('instruments.lotsIndex.table.received')}
                          </TableHead>
                          <TableHead className="w-28">
                            {t('instruments.lotsIndex.table.opened')}
                          </TableHead>
                          <TableHead className="min-w-[12rem]">
                            {t('instruments.controlsReagents.actionsHeading')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {lotDetailsVisibleThroughLotsTabCheckboxMask.map((lotDetail) => {
                          const status: LifecycleStatus = lotDetail.lifecycleStatus
                          const expirationDate =
                            lotDetail.openedExpiration ?? lotDetail.expirationDate
                          return (
                            <TableRow
                              key={lotDetail.id}
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() =>
                                setLotDialogState({ open: true, lotDetailId: lotDetail.id })
                              }
                            >
                              <TableCell className="font-medium">
                                {lotDetail.lotNumber ?? '—'}
                              </TableCell>
                              <TableCell>
                                <Badge variant={LIFECYCLE_BADGE[status] ?? 'outline'}>
                                  {t(`instruments.lotsIndex.lifecycleStatus.${status}`)}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <span>
                                    {expirationDate ? formatCalendarDate(expirationDate) : '—'}
                                  </span>
                                  <LotsExpireWarning
                                    expirationDate={expirationDate}
                                    lifecycleStatus={status}
                                    timeZone={displayTimeZone}
                                  />
                                </div>
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {lotDetail.siteName ?? '—'}
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {lotDetail.dateReceived
                                  ? formatCalendarDate(lotDetail.dateReceived)
                                  : '—'}
                              </TableCell>
                              <TableCell className="text-muted-foreground">
                                {lotDetail.dateOpened
                                  ? formatCalendarDate(lotDetail.dateOpened)
                                  : '—'}
                              </TableCell>
                              <TableCell
                                className="min-w-[12rem]"
                                onClick={(interactionCellEventBubbleGuard) =>
                                  interactionCellEventBubbleGuard.stopPropagation()
                                }
                              >
                                <div className="flex flex-wrap items-center justify-end gap-2">
                                  {/* REBUILD-366: see the mobile card above — the current
                                      selection keeps an enabled Select button. */}
                                  {lotsTabShowsPlanningBackedDefaultLotSelectionColumn && (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant={
                                        persistedDefaultLotDetailIdForMatchedPlanningTask ===
                                        lotDetail.id
                                          ? 'default'
                                          : 'secondary'
                                      }
                                      aria-pressed={
                                        persistedDefaultLotDetailIdForMatchedPlanningTask ===
                                        lotDetail.id
                                      }
                                      disabled={
                                        isUnusableLifecycleStatus(status) ||
                                        persistDefaultLotDetailSelectionRequestInFlight
                                      }
                                      onClick={() =>
                                        persistDefaultLotDetailSelectionForControlsReagentsPlanningTaskOnLotsTab(
                                          instrumentPlanningTaskPrimaryKeyForDefaultLotAssignment,
                                          lotDetail.id,
                                        )
                                      }
                                    >
                                      {persistedDefaultLotDetailIdForMatchedPlanningTask ===
                                      lotDetail.id
                                        ? t('instruments.controlsReagents.selectedButton')
                                        : t('instruments.controlsReagents.selectButton')}
                                    </Button>
                                  )}
                                  {canCreateLot ? (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() =>
                                        setLotDialogState({
                                          open: true,
                                          lotId: groupingLotRecordForAccordion.id,
                                          cloneLotDetailId: lotDetail.id,
                                        })
                                      }
                                    >
                                      {t('instruments.controlsReagents.cloneLotButton')}
                                    </Button>
                                  ) : null}
                                  {canRetireLot ? (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      disabled={isUnusableLifecycleStatus(status)}
                                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                                      onClick={() =>
                                        setScheduledRetirementTargetLotDetailIdOnInstrumentLotsTab(
                                          lotDetail.id,
                                        )
                                      }
                                    >
                                      {t('instruments.lotsIndex.retire')}
                                    </Button>
                                  ) : null}
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </>
                )}
              </div>
            </div>
          )
        },
      )}
      <DataPagination
        meta={buildMeta(accordionLotBucketsFilteredForLotsTabPaging, lotsPage)}
        onPageChange={setLotsPage}
      />
    </div>
  )

  const complianceContent = (
    <div className="rounded-lg border p-4 sm:p-6">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-6">
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('instruments.show.compliance.rollingPercent')}
          </dt>
          <dd
            className="mt-1 text-3xl font-bold text-gray-800"
            style={{
              backgroundColor: complianceBgColor(
                instrument.rollingCompliancePercent,
                complianceSettings?.colorCodes,
              ),
              display: 'inline-block',
              borderRadius: '9999px',
              padding: '0.25rem 0.75rem',
            }}
          >
            {complianceSettings && instrument.rollingCompliancePercent != null
              ? `Last ${complianceSettings.windowDays} Days · `
              : ''}
            {formatCompliancePercent(instrument.rollingCompliancePercent)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('instruments.show.compliance.failureCount')}
          </dt>
          <dd
            className={`mt-1 text-3xl font-bold ${instrument.rollingFailureCount ? 'text-red-600' : ''}`}
          >
            {instrument.rollingFailureCount ?? '—'}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('instruments.show.compliance.serviceReportCount')}
          </dt>
          <dd className="mt-1 text-3xl font-bold">{instrument.rollingServiceReportCount ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('instruments.show.compliance.openNonComplianceCount')}
          </dt>
          <dd
            className={`mt-1 text-3xl font-bold ${instrument.openNonComplianceCount ? 'text-red-600' : ''}`}
          >
            {instrument.openNonComplianceCount ?? '—'}
          </dd>
        </div>
      </dl>
      <div className="mt-5 border-t pt-4">
        <Button asChild variant="link" size="sm" className="h-auto px-0">
          <Link href={complianceDashboardPath()}>
            {t('instruments.show.compliance.viewDashboard')}
          </Link>
        </Button>
      </div>
    </div>
  )

  const historyContent = (
    <div className="space-y-4 sm:space-y-6">
      {/* Department Transfers */}
      <div className="space-y-2">
        <div className="min-w-0 overflow-hidden rounded-lg border">
          <button
            type="button"
            onClick={() => toggleHistorySection('dept')}
            className="flex w-full items-center justify-between p-4 text-left sm:cursor-default sm:pointer-events-none"
          >
            <h3 className="break-words font-semibold">
              {t('instruments.show.history.departmentTransfers')}
            </h3>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 sm:hidden ${
                openHistorySections.has('dept') ? 'rotate-180' : ''
              }`}
            />
          </button>
          <div className={`${openHistorySections.has('dept') ? '' : 'hidden'} sm:block`}>
            {departmentLogs.length === 0 ? (
              <p className="px-4 pb-4 text-sm text-muted-foreground">
                {t('instruments.show.history.noTransfers')}
              </p>
            ) : (
              <>
                <div className="divide-y border-t sm:hidden">
                  {getPage(departmentLogs, deptLogPage).map((log) => (
                    <div key={log.id} className="space-y-0.5 px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1 text-sm font-medium">
                        <span>{log.oldDepartmentName ?? '—'}</span>
                        <span className="text-muted-foreground">→</span>
                        <span>{log.newDepartmentName ?? '—'}</span>
                      </div>
                      <MobileField label={t('instruments.show.history.changedBy')}>
                        {log.changedByName ?? t('instruments.show.noData')}
                      </MobileField>
                      <MobileField label={t('instruments.show.history.changedAt')}>
                        {formatDateTime(log.changedAt)}
                      </MobileField>
                      {log.comments && (
                        <MobileField label={t('instruments.show.history.comment')}>
                          {log.comments}
                        </MobileField>
                      )}
                    </div>
                  ))}
                </div>
                <Table className="hidden sm:table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('instruments.show.history.from')}</TableHead>
                      <TableHead>{t('instruments.show.history.to')}</TableHead>
                      <TableHead>{t('instruments.show.history.changedBy')}</TableHead>
                      <TableHead>{t('instruments.show.history.changedAt')}</TableHead>
                      <TableHead>{t('instruments.show.history.comment')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getPage(departmentLogs, deptLogPage).map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          {log.oldDepartmentName ?? t('instruments.show.noData')} /{' '}
                          {log.oldUnitName ?? t('instruments.show.noData')}
                        </TableCell>
                        <TableCell>
                          {log.newDepartmentName ?? t('instruments.show.noData')} /{' '}
                          {log.newUnitName ?? t('instruments.show.noData')}
                        </TableCell>
                        <TableCell>{log.changedByName ?? t('instruments.show.noData')}</TableCell>
                        <TableCell>{formatDateTime(log.changedAt)}</TableCell>
                        <TableCell>{log.comments ?? t('instruments.show.noData')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </div>
        </div>
        {departmentLogs.length > 0 && openHistorySections.has('dept') && (
          <>
            <DataPagination
              meta={buildMeta(departmentLogs, deptLogPage)}
              onPageChange={setDeptLogPage}
            />
            {departmentLogs.length === 50 && (
              <p className="text-xs text-muted-foreground">
                {t('instruments.show.history.limitReached')}
              </p>
            )}
          </>
        )}
      </div>

      {/* Status Log */}
      <div className="space-y-2">
        <div className="min-w-0 overflow-hidden rounded-lg border">
          <button
            type="button"
            onClick={() => toggleHistorySection('status')}
            className="flex w-full items-center justify-between p-4 text-left sm:cursor-default sm:pointer-events-none"
          >
            <h3 className="break-words font-semibold">{t('instruments.show.history.statusLog')}</h3>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 sm:hidden ${
                openHistorySections.has('status') ? 'rotate-180' : ''
              }`}
            />
          </button>
          <div className={`${openHistorySections.has('status') ? '' : 'hidden'} sm:block`}>
            {statusLogs.length === 0 ? (
              <p className="px-4 pb-4 text-sm text-muted-foreground">
                {t('instruments.show.history.noStatusChanges')}
              </p>
            ) : (
              <>
                <div className="divide-y border-t sm:hidden">
                  {getPage(statusLogs, statusLogPage).map((log) => (
                    <div key={log.id} className="space-y-0.5 px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1 text-sm font-medium">
                        <span>{log.oldStatusLabel ?? '—'}</span>
                        <span className="text-muted-foreground">→</span>
                        <span>{log.newStatusLabel ?? '—'}</span>
                      </div>
                      <MobileField label={t('instruments.show.history.changedBy')}>
                        {log.changedByName ?? t('instruments.show.noData')}
                      </MobileField>
                      <MobileField label={t('instruments.show.history.completedAt')}>
                        {formatDateTime(log.completedAt)}
                      </MobileField>
                      {log.comments && (
                        <MobileField label={t('instruments.show.history.comment')}>
                          {log.comments}
                        </MobileField>
                      )}
                    </div>
                  ))}
                </div>
                <Table className="hidden sm:table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('instruments.show.history.from')}</TableHead>
                      <TableHead>{t('instruments.show.history.to')}</TableHead>
                      <TableHead>{t('instruments.show.history.changedBy')}</TableHead>
                      <TableHead>{t('instruments.show.history.completedAt')}</TableHead>
                      <TableHead>{t('instruments.show.history.comment')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getPage(statusLogs, statusLogPage).map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{log.oldStatusLabel ?? t('instruments.show.noData')}</TableCell>
                        <TableCell>{log.newStatusLabel ?? t('instruments.show.noData')}</TableCell>
                        <TableCell>{log.changedByName ?? t('instruments.show.noData')}</TableCell>
                        <TableCell>{formatDateTime(log.completedAt)}</TableCell>
                        <TableCell>{log.comments ?? t('instruments.show.noData')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </div>
        </div>
        {statusLogs.length > 0 && openHistorySections.has('status') && (
          <>
            <DataPagination
              meta={buildMeta(statusLogs, statusLogPage)}
              onPageChange={setStatusLogPage}
            />
            {statusLogs.length === 50 && (
              <p className="text-xs text-muted-foreground">
                {t('instruments.show.history.limitReached')}
              </p>
            )}
          </>
        )}
      </div>

      {/* Task History (Change Log) */}
      {taskVersionHistory && taskVersionHistory.length > 0 && (
        <div className="space-y-2">
          <div className="min-w-0 overflow-hidden rounded-lg border">
            <button
              type="button"
              onClick={() => toggleHistorySection('taskHistory')}
              className="flex w-full items-center justify-between p-4 text-left sm:cursor-default sm:pointer-events-none"
            >
              <h3 className="break-words font-semibold">
                {t('instruments.show.history.taskHistory')}
              </h3>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 sm:hidden ${
                  openHistorySections.has('taskHistory') ? 'rotate-180' : ''
                }`}
              />
            </button>
            <div className={`${openHistorySections.has('taskHistory') ? '' : 'hidden'} sm:block`}>
              {(() => {
                const allEntries = taskVersionHistory
                  .flatMap((version) =>
                    version.executionLogs.map((log, logIndex) => ({
                      version,
                      log,
                      key: `${version.taskId}-${logIndex}`,
                    })),
                  )
                  .sort((a, b) => b.log.performedAt.localeCompare(a.log.performedAt))
                const { visible, hiddenSuccessfulCount } = partitionTaskHistoryLogs(
                  allEntries,
                  showTaskHistoryAlertsOnly,
                )
                const countLabel =
                  hiddenSuccessfulCount === 1
                    ? t('instruments.show.history.successfulEventHidden', { count: 1 })
                    : t('instruments.show.history.successfulEventsHidden', {
                        count: hiddenSuccessfulCount,
                      })
                return (
                  <>
                    <div className="flex items-center gap-3 border-t px-4 py-3">
                      <Switch
                        id="task-history-alerts-only"
                        checked={showTaskHistoryAlertsOnly}
                        onCheckedChange={setShowTaskHistoryAlertsOnly}
                      />
                      <Label htmlFor="task-history-alerts-only" className="cursor-pointer text-sm">
                        <span className="font-semibold">
                          {showTaskHistoryAlertsOnly
                            ? t('instruments.show.history.showAlertsOnly')
                            : t('instruments.show.history.showFullDetails')}
                        </span>
                        {showTaskHistoryAlertsOnly && (
                          <span className="ml-1 text-muted-foreground">{countLabel}</span>
                        )}
                      </Label>
                    </div>
                    <div className="divide-y border-t">
                      {visible.length === 0 ? (
                        <p className="px-4 py-3 text-sm text-muted-foreground">
                          {t('instruments.show.history.noAlertEvents')}
                        </p>
                      ) : (
                        visible.map(({ version, log, key }) => (
                          <ChangeLogVersionCard
                            key={key}
                            instrumentId={instrument.id}
                            version={{ ...version, executionLogs: [log] }}
                          />
                        ))
                      )}
                    </div>
                  </>
                )
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // AC3: currentSchedule is a lazily-loaded, cached Inertia prop (see the effect above) that
  // is never refetched on its own. A task-log void/delete from the Reports-tab calendar bypasses
  // that cache entirely (raw fetch, not an Inertia visit), so it must be explicitly refreshed
  // here — otherwise the Tasks tab keeps showing a stale complianceWindow.lastCompletedAt.
  // Skip the reload if currentSchedule was never fetched — nothing stale to refresh.
  const refreshCurrentScheduleAfterCalendarReview = () => {
    if (!currentSchedule) return
    router.reload({ only: ['currentSchedule'] })
  }

  const reportsContent = canReadInstrumentReports ? (
    <ReportsTabContent
      instrumentId={instrument.id}
      instrumentLabel={instrument.instrumentLabel}
      serialNumber={instrument.serialNumber ?? null}
      reportsCalendar={reportsCalendar ?? null}
      reportsSiteToday={reportsSiteToday}
      serviceReports={serviceReports ?? []}
      isActive={activeTab === 'reports'}
      onAfterReview={refreshCurrentScheduleAfterCalendarReview}
      calendarRefreshSignal={calendarRefreshSignal}
    />
  ) : null

  const tabContent: Record<TabValue, React.ReactNode> = {
    info: infoContent,
    tasks: tasksContent,
    lots: lotsContent,
    compliance: complianceContent,
    reports: reportsContent,
    history: historyContent,
  }

  // ─── Tab label helpers ────────────────────────────────────────────────────────

  const tabLabel = (tab: TabValue) => <>{t(`instruments.show.tabs.${tab}`)}</>

  // ─── Content wrapper per tab (desktop TabsContent uses border for info/compliance) ──

  const desktopTabClass: Record<TabValue, string> = {
    info: 'mt-2 min-w-0',
    tasks: 'mt-2 min-w-0 max-w-full',
    lots: 'mt-2 min-w-0 max-w-full space-y-4',
    compliance: 'mt-2 min-w-0',
    reports: 'mt-2 min-w-0 space-y-4',
    history: 'mt-2 min-w-0',
  }

  const visibleTabs = VALID_TABS.filter((tab) => tab !== 'reports' || canReadInstrumentReports)

  const headerLocation = [instrument.siteName, instrument.departmentName, instrument.unitName]
    .filter(Boolean)
    .join(' / ')

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-w-0 space-y-6">
      {/* Header card */}
      <div className="min-w-0 max-w-full overflow-hidden rounded-lg border">
        <div className="flex min-h-0 min-w-0 flex-row">
          {instrument.imgUrl ? (
            <img
              src={instrument.imgUrl}
              alt=""
              className="max-h-24 w-24 shrink-0 self-start object-contain sm:max-h-44 sm:w-44"
            />
          ) : (
            <div className="flex h-24 w-24 shrink-0 items-center justify-center self-start bg-muted sm:h-44 sm:w-44">
              <ImageOff className="h-6 w-6 text-muted-foreground/40 sm:h-8 sm:w-8" />
            </div>
          )}
          <div className="flex min-w-0 flex-1 flex-col gap-3 p-3 sm:gap-4 sm:p-6">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg font-bold [overflow-wrap:anywhere] sm:text-2xl">
                {instrument.instrumentLabel}
              </h1>
              {instrument.serialNumber && (
                <p className="text-xs text-muted-foreground [overflow-wrap:anywhere] sm:text-sm">
                  {instrument.serialNumber}
                </p>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {instrument.statusLabel && (
                  <Badge variant="secondary">{instrument.statusLabel}</Badge>
                )}
                {instrument.instrumentTags.map((tag) => (
                  <Badge key={tag.id} variant="secondary">
                    {tag.name}
                  </Badge>
                ))}
                <div
                  className="mt-0.5 flex w-full min-w-0 flex-wrap items-center gap-2"
                  role="status"
                >
                  <span className="text-xs text-muted-foreground sm:text-sm">
                    {t('instruments.table.taskStatus')}
                  </span>
                  <TaskStatusBadge taskStatusCode={instrument.taskStatusCode} />
                </div>
                {instrument.instrumentTypeName && (
                  <p className="w-full text-xs text-muted-foreground [overflow-wrap:anywhere] sm:text-sm">
                    {t('instruments.show.info.type')}: {instrument.instrumentTypeName}
                  </p>
                )}
                {headerLocation && (
                  <p className="w-full text-xs text-muted-foreground [overflow-wrap:anywhere] sm:text-sm">
                    {t('instruments.show.info.location')}: {headerLocation}
                  </p>
                )}
              </div>
              {/* Legacy parity: a QC-failure warning that links straight to the Reports tab
                  (instrumentDetail.template.html:48-60 — a routerLink, not a modal). Count is
                  the rolling-window failure metric (REBUILD-586), labeled in days exactly like
                  legacy's "Failed {n} time(s) in the Past {days} days". */}
              {(instrument.rollingFailureCount ?? 0) > 0 && (
                <Alert variant={failuresAllResolved ? 'default' : 'destructive'} className="mt-3">
                  {failuresAllResolved ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <AlertTriangle className="h-4 w-4" />
                  )}
                  <AlertTitle>
                    {failuresAllResolved
                      ? t('instruments.show.failureWarning.resolvedTitle')
                      : t('instruments.show.failureWarning.title')}
                  </AlertTitle>
                  <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span>
                      {t(
                        failuresAllResolved
                          ? 'instruments.show.failureWarning.resolvedMessage'
                          : 'instruments.show.failureWarning.message',
                        {
                          count: instrument.rollingFailureCount,
                          days: instrument.rollingFailureWindowDays,
                        },
                      )}
                    </span>
                    {canReadInstrumentReports && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className={cn(
                          'h-auto p-0 underline',
                          failuresAllResolved ? 'text-foreground' : 'text-destructive',
                        )}
                        onClick={openFailureDetails}
                      >
                        {t('instruments.show.failureWarning.viewDetails')}
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
            <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
              {canTransfer && (
                <Button asChild variant="outline" size="sm" className="w-full min-w-0 sm:w-auto">
                  <Link href={transferFormInstrumentPath(instrument.id)}>
                    {t('instruments.show.transferButton')}
                  </Link>
                </Button>
              )}
              {canUpdateStatus &&
                (isStatusChangeLocked ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full min-w-0 sm:w-auto"
                    disabled
                    title={t('instruments.show.updateStatusLockedHint')}
                  >
                    {t('instruments.show.updateStatusButton')}
                  </Button>
                ) : (
                  <Button asChild variant="outline" size="sm" className="w-full min-w-0 sm:w-auto">
                    <Link href={statusUpdateFormInstrumentPath(instrument.id)}>
                      {t('instruments.show.updateStatusButton')}
                    </Link>
                  </Button>
                ))}
              {canClone && (
                <Button asChild variant="outline" size="sm" className="w-full min-w-0 sm:w-auto">
                  <Link href={cloneInstrumentPath(instrument.id)}>
                    {t('instruments.show.cloneButton')}
                  </Link>
                </Button>
              )}
              {canEdit && (
                <Button asChild variant="outline" size="sm" className="w-full min-w-0 sm:w-auto">
                  <Link href={editInstrumentPath(instrument.id)}>
                    {t('instruments.show.editButton')}
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: select nav (hidden on sm+) */}
      <div className="sm:hidden space-y-5" data-instrument-reports-tab="">
        <Select value={activeTab} onValueChange={handleTabChange}>
          <SelectTrigger className="w-full">
            <span>{t(`instruments.show.tabs.${activeTab}`)}</span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tasks">
              <span className="flex items-center gap-2">
                {t('instruments.show.tabs.tasks')}
                {instrument.tasks.length > 0 && (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium leading-none text-muted-foreground">
                    {instrument.tasks.length}
                  </span>
                )}
              </span>
            </SelectItem>
            <SelectItem value="lots">
              <span className="flex items-center gap-2">
                {t('instruments.show.tabs.lots')}
                {accordionLotBucketsFilteredForLotsTabPaging.length > 0 && (
                  <span className="rounded-full bg-muted px-1.5 py-0.5 text-xs font-medium leading-none text-muted-foreground">
                    {accordionLotBucketsFilteredForLotsTabPaging.length}
                  </span>
                )}
              </span>
            </SelectItem>
            {canReadInstrumentReports && (
              <SelectItem value="reports">{t('instruments.show.tabs.reports')}</SelectItem>
            )}
            <SelectItem value="compliance">{t('instruments.show.tabs.compliance')}</SelectItem>
            <SelectItem value="history">{t('instruments.show.tabs.history')}</SelectItem>
            <SelectItem value="info">{t('instruments.show.tabs.info')}</SelectItem>
          </SelectContent>
        </Select>
        {/* AC4: the Reports tab's calendar (InstrumentCalendarViewPanel) keeps its own local
            date-range/task-filter state, which would otherwise reset every time this single-slot
            render swaps activeTab away and back. Keep it permanently mounted (hidden via the
            native `hidden` attribute when inactive, same mechanism Radix uses below for the
            desktop Tabs' forceMount) instead of only inserting it here on demand, so the same
            instance — and its state — survives a tab switch. */}
        <div hidden={activeTab !== 'reports'} data-instrument-reports-panel="" tabIndex={-1}>
          {reportsContent}
        </div>
        {activeTab !== 'reports' && <div>{tabContent[activeTab]}</div>}
      </div>

      {/* Desktop: tabs (hidden on mobile) */}
      <div className="hidden sm:block" data-instrument-reports-tab="">
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="w-full min-w-0 max-w-full"
        >
          <TabsList className="!flex h-9 w-full min-w-0 max-w-full flex-nowrap justify-start gap-0 overflow-x-auto overscroll-x-contain bg-muted p-1 [scrollbar-width:none]">
            {visibleTabs.map((tab) => (
              <TabsTrigger
                key={tab}
                value={tab}
                className="h-[calc(100%-2px)] shrink-0 px-3 py-1 text-sm"
              >
                {tabLabel(tab)}
              </TabsTrigger>
            ))}
          </TabsList>
          {visibleTabs.map((tab) => {
            const keepReportsMounted = tab === 'reports'
            return (
              <TabsContent
                key={tab}
                value={tab}
                className={desktopTabClass[tab]}
                // AC4: Radix unmounts inactive TabsContent by default, which discards
                // InstrumentCalendarViewPanel's local date-range/task-filter state on every
                // Tasks ⇄ Reports switch. forceMount keeps it mounted; Radix does not hide
                // forceMounted-but-inactive content on its own (it only flips data-state), so
                // the native `hidden` attribute below does the hiding.
                forceMount={keepReportsMounted ? true : undefined}
                hidden={keepReportsMounted ? activeTab !== 'reports' : undefined}
              >
                {tabContent[tab]}
              </TabsContent>
            )
          })}
        </Tabs>
      </div>
      {scheduledRetirementTargetLotDetailIdOnInstrumentLotsTab != null ? (
        <RetireLotDetailDialog
          isOpen
          closeDialogCallback={() =>
            setScheduledRetirementTargetLotDetailIdOnInstrumentLotsTab(null)
          }
          retireSubmissionPathForLotDetail={retireInstrumentLotPath(
            Number(instrument.id),
            scheduledRetirementTargetLotDetailIdOnInstrumentLotsTab,
          )}
          afterSuccessfulSubmissionCallback={() => router.reload({ only: ['instrument', 'lots'] })}
        />
      ) : null}
      <LotFormDialog
        open={lotDialogState.open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setLotDialogState({ open: false })
        }}
        instrumentId={instrument.id}
        {...(lotDialogState.open && 'lotDetailId' in lotDialogState && lotDialogState.lotDetailId
          ? { lotDetailId: lotDialogState.lotDetailId }
          : {
              lotId: lotDialogState.open && 'lotId' in lotDialogState ? lotDialogState.lotId : 0,
              cloneLotDetailId:
                lotDialogState.open && 'cloneLotDetailId' in lotDialogState
                  ? lotDialogState.cloneLotDetailId
                  : undefined,
            })}
        onSuccess={(newLotDetailId) => {
          const parentLotId =
            lotDialogState.open && 'lotId' in lotDialogState ? lotDialogState.lotId : null
          setLotDialogState({ open: false })

          if (newLotDetailId != null && parentLotId != null) {
            // Legacy parity: selection is per-(instrument, lot), so any task using the lot can
            // carry the write. Auto-assign the newly created detail via the first candidate task.
            const candidateTask = instrument.tasks.find(
              (task) => task.linkedLotId === parentLotId || task.usedLotIds.includes(parentLotId),
            )

            if (candidateTask != null) {
              persistDefaultLotDetailSelectionForControlsReagentsPlanningTaskOnLotsTab(
                candidateTask.id,
                newLotDetailId,
              )
              return
            }
          }

          router.reload({ only: ['instrument', 'lots'] })
        }}
      />

      {compareDialogLotDetailId != null && (
        <LotCompareDialog
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setCompareDialogLotDetailId(null)
          }}
          instrumentId={instrument.id}
          lotDetailId={compareDialogLotDetailId}
        />
      )}

      {canCreateServiceReport && (
        <AddServiceReportDialog
          instrumentId={Number(instrument.id)}
          loggerOptions={serviceReportLoggerOptions ?? []}
          siteTimeZone={displayTimeZone ?? 'UTC'}
          allowedFileTypes={serviceReportAllowedFileTypes ?? { mimeTypes: [], extensions: [] }}
          open={serviceReportDialogOpen}
          onOpenChange={setServiceReportDialogOpen}
        />
      )}

      {/* REBUILD-554: header "View Details" opens the exact failed occurrence directly. */}
      {failureDetailsOpen && hasFailureTarget && canReadInstrumentReports && (
        <LogDetailsDialog
          instrumentId={Number(instrument.id)}
          instrumentTaskId={instrument.failureTargetTaskId ?? null}
          instrumentLabel={instrument.instrumentLabel}
          serialNumber={instrument.serialNumber ?? null}
          taskLogId={failureTargetLogId}
          date={failureTargetDate}
          fromDate={failureTargetDate}
          toDate={failureTargetDate}
          dateToLogIdMap={{ [failureTargetDate]: failureTargetLogId }}
          failureOnly
          onClose={() => setFailureDetailsOpen(false)}
          onAfterDelete={() => {
            setFailureDetailsOpen(false)
            reloadInstrumentAndScheduleAfterVoid()
            bumpCalendarRefreshSignal()
          }}
        />
      )}
    </div>
  )
}

export default InstrumentShowPage
